import { getRaffleResultsService } from 'pages/Raffle/services/raffle';
import { useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { TRaffleResultItem } from '../../types/common.types';

type GetRaffleResults = {
  isQueryingRaffleResults: boolean;
  handleGetCompletedRaffleResults: () => void;
  hasMoreRaffleResults?: boolean;
  hasErrorQueryingRaffleResults: boolean;
  raffleResults: TRaffleResultItem[] | undefined;
};

type GetRaffleResultsProps = {
  pageSize: number;
};

function useGetRaffleResults({
  pageSize,
}: GetRaffleResultsProps): GetRaffleResults {
  const [error, setError] = useState({ hasError: false, errorMessage: '' });
  const [pageData, setPageData] = useState<Record<string, TRaffleResultItem>>(
    {}
  );

  const {
    data,
    refetch: refetchRaffleResults,
    fetchNextPage: fetchNextRaffleResults,
    isLoading: isQueryingRaffleResults,
    hasNextPage: hasMoreRaffleResults,
  } = useInfiniteQuery(
    'getRaffleResults',
    ({ pageParam: page = 0 }) => {
      return getRaffleResultsService({
        page,
        pageSize,
      });
    },
    {
      enabled: false,
      suspense: false,
      getNextPageParam: (_, pages) => {
        const { hasNext } = pages[pages.length - 1].data.result;
        if (hasNext) {
          return pages.length;
        }
        return undefined;
      },
      onSuccess: (resp) => {
        const lastPage = resp.pages.slice(-1)[0];
        if (lastPage.data?.success) {
          const newState = lastPage.data.result.completedDrawDetails.reduce(
            (state, currentDrawDetails) => {
              const { date, drawDetails } = currentDrawDetails;
              state[date] = {
                ...currentDrawDetails,
                drawDetails: [
                  ...(state[date] ? state[date].drawDetails : []),
                  ...drawDetails,
                ],
              };
              return state;
            },
            { ...pageData }
          );
          setPageData(newState);
          setError({ hasError: false, errorMessage: '' });
        } else {
          setError({
            hasError: true,
            errorMessage: resp.pages.slice(-1)[0].data.errorMessage,
          });
        }
      },
    }
  );

  const raffleResults = useMemo(() => Object.values(pageData), [pageData]);

  function handleGetCompletedRaffleResults() {
    if (hasMoreRaffleResults || !data) {
      fetchNextRaffleResults();
    } else if (error.hasError) {
      refetchRaffleResults();
    }
  }

  return {
    isQueryingRaffleResults,
    handleGetCompletedRaffleResults,
    hasErrorQueryingRaffleResults: error.hasError,
    raffleResults,
    hasMoreRaffleResults,
  };
}

export default useGetRaffleResults;
